import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { GH } from '../theme';
import { useWindowWidth } from '../hooks/useWindowWidth';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { Reveal } from '../components/Reveal';
import { SectionLabel } from '../components/SectionLabel';
import { GHButton } from '../components/GHButton';
import { ImagePlaceholder } from '../components/ImagePlaceholder';

const VALUES = [
  { title: 'Precision', desc: 'Every measurement, every finish, every decision made with exacting care.' },
  { title: 'Reliability', desc: 'We show up, we deliver, we follow through. Every time, without exception.' },
  { title: 'Safety First', desc: 'Zero compromise on site safety. Full compliance with NZ health & safety.' },
  { title: 'Professionalism', desc: 'Clear communication, clean sites, and respectful teams on every job.' },
];

export default function AboutPage({ navigate }) {
  const w = useWindowWidth();
  const mob = w < 768;
  const tab = w < 1024 && w >= 768;
  return (
    <div style={{ background: GH.colors.bg, minHeight: '100vh' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(160deg, #1C1F23 0%, #2A2D31 100%)', padding: mob ? '130px 20px 72px' : '160px 40px 100px', position: 'relative', overflow: 'hidden' }}>
        <div className="hero-grid" style={{ position: 'absolute', inset: 0, opacity: 0.4 }} />
        <div style={{ position: 'absolute', top: 0, left: '45%', width: 1, height: '100%', background: 'linear-gradient(180deg, transparent, rgba(74,124,111,0.12), transparent)' }} />
        <div style={{ maxWidth: 1300, margin: '0 auto', position: 'relative', zIndex: 1 }}>
          <Reveal>
            <SectionLabel>About Us</SectionLabel>
            <h1 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: mob ? 'clamp(32px, 8vw, 48px)' : 'clamp(44px, 5.5vw, 76px)', color: '#fff', letterSpacing: '-0.035em', lineHeight: 1.02, margin: '24px 0 24px' }}>
              Built on Precision.<br />
              <span style={{ color: GH.colors.gold }}>Defined by Results.</span>
            </h1>
            <p style={{ fontFamily: GH.fonts.body, fontSize: mob ? 15 : 17, color: GH.colors.onDark.body, lineHeight: 1.65, maxWidth: 580, margin: 0 }}>
              GreyHolm Group brings construction and property services together under one accountable team. We have people with decades of experience across major construction projects in Auckland, built to deliver from day one.
            </p>
          </Reveal>
        </div>
      </div>
      {/* Story */}
      <section style={{ padding: mob ? '64px 20px' : '120px 40px', background: GH.colors.bg }}>
        <div style={{ maxWidth: 1300, margin: '0 auto', display: 'grid', gridTemplateColumns: mob ? '1fr' : '1fr 1.2fr', gap: mob ? 40 : 80, alignItems: 'start' }}>
          <Reveal>
            <div style={{ background: GH.colors.charcoal, borderRadius: 4, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <img src="/images/Logo.png" alt="GreyHolm Group" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <SectionLabel>Our Story</SectionLabel>
              <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(28px, 3.5vw, 46px)', color: GH.colors.white, letterSpacing: '-0.03em', lineHeight: 1.06, margin: '20px 0 24px' }}>
                Decades of Experience,<br />A Fresh Approach
              </h2>
              <p style={{ fontFamily: GH.fonts.body, fontSize: 15, color: GH.colors.subDk, lineHeight: 1.8, margin: '0 0 20px' }}>
                Our founder has spent decades managing major construction projects across Auckland. With a deep background in construction management and project delivery, the vision is clear: Auckland needs a property services company that treats every job, no matter the size, as a flagship project.
              </p>
              <p style={{ fontFamily: GH.fonts.body, fontSize: 15, color: GH.colors.subDk, lineHeight: 1.8, margin: '0 0 20px' }}>
                We specialise in effective cost management backed by professional expertise built over decades. Every project is managed with the same rigour and accountability you'd expect on a major commercial build: clear budgets, transparent reporting, and no surprises.
              </p>
              <p style={{ fontFamily: GH.fonts.body, fontSize: 15, color: GH.colors.subDk, lineHeight: 1.8, margin: '0 0 32px' }}>
                GreyHolm Group brings experienced hands to every engagement. From project planning through to final handover, our team delivers the judgement of seasoned professionals who understand Auckland's construction landscape inside and out.
              </p>
              <GHButton onClick={() => navigate('contact')}>Talk to Our Team</GHButton>
            </Reveal>
          </div>
        </div>
      </section>
      {/* Values */}
      <section style={{ padding: mob ? '64px 20px' : '100px 40px', background: GH.colors.surface }}>
        <div style={{ maxWidth: 1300, margin: '0 auto' }}>
          <Reveal style={{ textAlign: 'center', marginBottom: mob ? 40 : 64 }}>
            <SectionLabel>Our Values</SectionLabel>
            <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(28px, 3.5vw, 46px)', color: GH.colors.white, letterSpacing: '-0.03em', margin: '20px 0 0' }}>
              The Principles We Work By
            </h2>
          </Reveal>
          <div style={{ display: 'grid', gridTemplateColumns: mob ? '1fr' : tab ? 'repeat(2, 1fr)' : 'repeat(4, 1fr)', gap: 0 }}>
            {VALUES.map((v, i) => (
              <Reveal key={i} delay={i * 80}>
                <div style={{ background: GH.colors.card, padding: mob ? '32px 24px' : '44px 36px', borderTop: `3px solid ${GH.colors.gold}`, borderRight: (!mob && !tab && i < 3) || (tab && i % 2 === 0) ? `1px solid ${GH.colors.borderDk}` : 'none', borderBottom: (mob || (tab && i < 2)) ? `1px solid ${GH.colors.borderDk}` : 'none', height: '100%', boxSizing: 'border-box' }}>
                  <div style={{ width: 44, height: 44, background: GH.colors.goldMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 24, fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 18, color: GH.colors.gold }}>0{i+1}</div>
                  <h3 style={{ fontFamily: GH.fonts.display, fontWeight: 700, fontSize: 19, color: GH.colors.white, letterSpacing: '-0.015em', margin: '0 0 14px' }}>{v.title}</h3>
                  <p style={{ fontFamily: GH.fonts.body, fontSize: 14, color: GH.colors.mutedDk, lineHeight: 1.7, margin: 0 }}>{v.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
      {/* CTA */}
      <section style={{ padding: mob ? '64px 20px' : '100px 40px', background: GH.colors.bg, textAlign: 'center' }}>
        <Reveal>
          <h2 style={{ fontFamily: GH.fonts.display, fontWeight: 800, fontSize: 'clamp(28px, 4vw, 52px)', color: GH.colors.white, letterSpacing: '-0.035em', margin: '0 0 24px' }}>Work With Us</h2>
          <p style={{ fontFamily: GH.fonts.body, fontSize: 16, color: GH.colors.mutedDk, marginBottom: 36 }}>Let's discuss your property's requirements.</p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <GHButton size={mob ? 'md' : 'lg'} onClick={() => navigate('quote')}>Get a Quote</GHButton>
            <GHButton size={mob ? 'md' : 'lg'} variant="ghost" onClick={() => navigate('contact')}>Contact Us</GHButton>
          </div>
        </Reveal>
      </section>
    </div>
  );
}

